import { IDocumentResource } from '@/api/resources/types/document-resource';
import { IImplementResourceInDocumentDTO } from '@/api/resources/dtos/implement-resource-in-document-dto';
import { IImplementResourceDTO } from '@/api/resources/dtos/implement-resource-dto';
import { ICreateResourceDTO } from '@/api/resources/dtos/create-resource-dto';
import { IUpdateResourceDTO } from '@/api/resources/dtos/update-resource-dto';
import { ResourceMutations } from '@/api/resources/graphql/resource.mutations';
import { ResourceQueries } from '@/api/resources/graphql/resource.queries';
import { EResourceSortAttribute } from '@/api/resources/types/enum/resource-sort-attribute';
import { EResourceType } from '@/api/resources/types/enum/resource-type';
import { IResource } from '@/api/resources/types/resource';
import { apolloClient } from '@/apollo';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';

export class ResourceService {
  static async loadList(
    searchTerms: string[],
    limit: number,
    offset: number,
    sortBy: EResourceSortAttribute,
    sortOrder: string,
    type: EResourceType,
    directory?: string
  ): Promise<{ resources: IResource[]; totalCount: number }> {
    const resourceType: EResourceType = type || undefined;
    const {
      data: {
        resources: { resources, totalCount },
      },
    } = await apolloClient.query({
      query: ResourceQueries.loadResourcesQuery,
      variables: {
        directory,
        resourceType,
        searchTerms,
        limit,
        offset,
        sortBy,
        sortOrder,
      },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return { resources, totalCount };
  }

  static async loadDetails(id: string): Promise<IResource> {
    const {
      data: { resource },
    } = await apolloClient.query({
      query: ResourceQueries.loadResourceQuery,
      variables: { id },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return resource;
  }

  static async create(payload: ICreateResourceDTO): Promise<IResource> {
    const {
      data: { createResource },
    } = await apolloClient.mutate({
      mutation: ResourceMutations.createResourceMutation,
      variables: { payload },
    });
    return createResource;
  }

  static async update(payload: IUpdateResourceDTO): Promise<IResource> {
    const {
      data: { updateResource },
    } = await apolloClient.mutate({
      mutation: ResourceMutations.updateResourceMutation,
      variables: { payload },
    });
    return updateResource;
  }

  static async delete(id: string): Promise<void> {
    await apolloClient.mutate({
      mutation: ResourceMutations.deleteResourceMutation,
      variables: { id },
    });
  }

  static async implementInDocument(payload: IImplementResourceInDocumentDTO): Promise<void> {
    await apolloClient.mutate({
      mutation: ResourceMutations.implementResourceInDocumentMutation,
      variables: { payload },
    });
  }

  static async implementInTemplate(payload: IImplementResourceDTO): Promise<void> {
    await apolloClient.mutate({
      mutation: ResourceMutations.implementResourceInTemplateMutation,
      variables: { payload },
    });
  }

  static async loadDocumentResources({
    documentId,
    resourceId,
    contentId,
    inTemplate,
  }: {
    documentId?: string;
    resourceId?: string;
    contentId?: string;
    inTemplate?: boolean;
  }): Promise<IDocumentResource[]> {
    const {
      data: { documentResources },
    } = await apolloClient.query({
      query: ResourceQueries.documentResourcesQuery,
      variables: { documentId, resourceId, contentId, inTemplate },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return documentResources;
  }

  static async disconnect(payload: IImplementResourceDTO): Promise<void> {
    await apolloClient.mutate({
      mutation: ResourceMutations.disconnectResourceMutation,
      variables: { payload },
    });
  }

  static async createResourceDirectory(title: string, directory: string): Promise<IResource> {
    const {
      data: { createResourceDirectory },
    } = await apolloClient.mutate({
      mutation: ResourceMutations.createResourceDirectory,
      variables: { payload: { title, directory } },
    });
    return createResourceDirectory;
  }
}
